import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { CreditCard, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const Checkout = () => {
  const { cart, getTotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');

  React.useEffect(() => {
    if (cart.length === 0) {
      navigate('/carrinho');
    }
  }, [cart, navigate]);

  const handlePayment = async (e) => {
    e.preventDefault();
    setProcessing(true);

    // Mock: simula processamento de pagamento
    setTimeout(() => {
      // Salva compra no localStorage
      const purchases = JSON.parse(localStorage.getItem('purchases') || '[]');
      const newPurchase = {
        id: Date.now(),
        userId: user.id,
        items: cart,
        total: getTotal(),
        paymentMethod,
        date: new Date().toISOString(),
        status: 'completed'
      };
      purchases.push(newPurchase);
      localStorage.setItem('purchases', JSON.stringify(purchases));

      // Atualiza e-books do usuário
      const updatedUser = {
        ...user,
        purchasedEbooks: [...(user.purchasedEbooks || []), ...cart.map(item => item.id)]
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      clearCart();
      setProcessing(false);
      toast.success('Pagamento realizado com sucesso!');
      navigate('/meus-ebooks');
    }, 2000);
  };

  return (
    <div style={{ background: 'var(--bg-page)', minHeight: '100vh', paddingTop: '120px', paddingBottom: '64px' }}>
      <div className="container" style={{ maxWidth: '800px' }}>
        <h1 className="display-medium" style={{ marginBottom: '32px' }}>
          Finalizar Compra
        </h1>

        <div style={{ display: 'grid', gap: '24px' }}>
          {/* Resumo do Pedido */}
          <div className="network-card">
            <h2 className="heading-2" style={{ marginBottom: '20px' }}>
              Resumo do Pedido
            </h2>
            {cart.map(item => (
              <div key={item.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
                paddingBottom: '16px',
                borderBottom: '1px solid var(--border-light)'
              }}>
                <div>
                  <p className="body-medium" style={{ fontWeight: '600' }}>{item.title}</p>
                  <p className="caption" style={{ color: 'var(--text-light)' }}>{item.target}</p>
                </div>
                <p className="body-medium" style={{ fontWeight: '600' }}>R$ {item.price.toFixed(2)}</p>
              </div>
            ))}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              paddingTop: '16px'
            }}>
              <span className="heading-3">Total</span>
              <span className="heading-2" style={{ color: 'var(--brand-dark)' }}>
                R$ {getTotal().toFixed(2)}
              </span>
            </div>
          </div>

          {/* Método de Pagamento */}
          <div className="network-card">
            <h2 className="heading-2" style={{ marginBottom: '20px' }}>
              Método de Pagamento
            </h2>
            <p className="body-small" style={{ 
              background: '#fef3c7', 
              padding: '12px 16px', 
              borderRadius: '12px',
              marginBottom: '20px',
              border: '1px solid #fbbf24'
            }}>
              <strong>Modo Mock:</strong> Este é um pagamento simulado. Em produção, a integração real com Stripe/PayPal será ativada.
            </p>

            <form onSubmit={handlePayment}>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block',
                  padding: '16px',
                  border: `2px solid ${paymentMethod === 'stripe' ? 'var(--brand-dark)' : 'var(--border-medium)'}`,
                  borderRadius: '16px',
                  marginBottom: '12px',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s ease'
                }}>
                  <input
                    type="radio"
                    name="payment"
                    value="stripe"
                    checked={paymentMethod === 'stripe'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    style={{ marginRight: '12px' }}
                  />
                  <CreditCard size={20} style={{ display: 'inline', marginRight: '8px' }} />
                  <span className="body-medium" style={{ fontWeight: '600' }}>Cartão de Crédito (Stripe)</span>
                </label>

                <label style={{ 
                  display: 'block',
                  padding: '16px',
                  border: `2px solid ${paymentMethod === 'paypal' ? 'var(--brand-dark)' : 'var(--border-medium)'}`,
                  borderRadius: '16px',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s ease'
                }}>
                  <input
                    type="radio"
                    name="payment"
                    value="paypal"
                    checked={paymentMethod === 'paypal'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    style={{ marginRight: '12px' }}
                  />
                  <span className="body-medium" style={{ fontWeight: '600' }}>PayPal</span>
                </label>
              </div>

              {paymentMethod === 'stripe' && (
                <div style={{ 
                  background: 'var(--bg-subtle)', 
                  padding: '24px', 
                  borderRadius: '16px',
                  marginBottom: '24px'
                }}>
                  <div style={{ marginBottom: '16px' }}>
                    <label className="body-small" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      Número do Cartão (Mock)
                    </label>
                    <input
                      type="text"
                      placeholder="1234 5678 9012 3456"
                      defaultValue="4242 4242 4242 4242"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: '2px solid var(--border-medium)',
                        fontSize: '16px'
                      }}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label className="body-small" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        Validade (Mock)
                      </label>
                      <input
                        type="text"
                        placeholder="MM/AA"
                        defaultValue="12/25"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '12px',
                          border: '2px solid var(--border-medium)',
                          fontSize: '16px'
                        }}
                      />
                    </div>
                    <div>
                      <label className="body-small" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        CVV (Mock)
                      </label>
                      <input
                        type="text"
                        placeholder="123"
                        defaultValue="123"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '12px',
                          border: '2px solid var(--border-medium)',
                          fontSize: '16px'
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              <button 
                type="submit"
                className="btn-cta"
                disabled={processing}
                style={{ 
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: processing ? 0.6 : 1
                }}
              >
                {processing ? (
                  <>Processando...</>
                ) : (
                  <>
                    <CheckCircle size={20} />
                    Confirmar Pagamento - R$ {getTotal().toFixed(2)}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;